import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  saveCompanyUpdate,
  generateCompanyUpdate,
  DEFAULT_META,
} from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import {
  rememberCompanyUpdate,
  clearCompanyUpdateOverride,
} from '../data/companyUpdateOverride.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useSaveCompanyUpdate — save (or clear) the meta.companyUpdate slot
 * via the server-side partial update. Optimistic cache write; rolls
 * back on error. Same shape as useSaveExecSummary, but on the
 * companyUpdate slot.
 *
 * Usage:
 *   const save = useSaveCompanyUpdate()
 *   save.mutate({ markdown, generatedAt, generatedBy, edited: true })
 *   save.mutate({ clear: true })
 */
export function useSaveCompanyUpdate() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const companyUpdate = await saveCompanyUpdate(payload)
      return companyUpdate
    },

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      // Drop any prior shield so the optimistic value is the visible
      // state until onSuccess re-establishes the override against the
      // server's authoritative response.
      clearCompanyUpdateOverride()

      useHistoryStore
        .getState()
        .pushSnapshot(
          'Company update',
          queryClient.getQueryData(['goals']),
          previous
        )

      const next = payload?.clear ? null : payload
      queryClient.setQueryData(['meta'], {
        ...previous,
        companyUpdate: next,
      })
      return { previous }
    },

    onSuccess: (companyUpdate) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], { ...current, companyUpdate })
      // Shield against the meta query's 60-second refetch landing on
      // stale KV data and silently reverting this save.
      rememberCompanyUpdate(companyUpdate ?? null)
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
  return withArchiveGuard(mutation)
}

/**
 * useGenerateCompanyUpdate — POST to /ai-company-update with this
 * week's inputs and receive a fresh markdown draft. Does NOT auto-save;
 * the component decides whether to call useSaveCompanyUpdate after the
 * user reviews.
 */
export function useGenerateCompanyUpdate() {
  const mutation = useMutation({
    mutationFn: async (args) => generateCompanyUpdate(args || {}),
  })
  return withArchiveGuard(mutation)
}
